import React from 'react';
import Modal from '../../../../shared/modal';
import { Controller, useForm } from 'react-hook-form';
import DatePopover from '../components/DatePopover';
import { tryToUploadManualTransaction } from '../../../../store/brokers/partners/transactions/transactionsSlice';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import Loading from '../../../../shared/Loading';

interface IColumnPickerModal {
	openModal: boolean;
	handleCloseModal: () => void;
}

type FormData = {
	transactionType: string;
	transactionDate: Date;
	direction: string;
	amount: number;
	description: string;
  };

const ManualTransactionModal = ({
	openModal,
	handleCloseModal,
}: IColumnPickerModal) => {
	const {transactionIsLoading} = useAppSelector((state) => state.transactions)
	const dispatch = useAppDispatch();
	const onCloseModal = () => {
		handleCloseModal();
	};

	const {
		control,
		register,
		handleSubmit,
		formState: { errors },
	  } = useForm<FormData>();
	  const onSubmit = async (data: FormData) => {
		try {
			await dispatch(tryToUploadManualTransaction(data)).unwrap();
			onCloseModal()
		} catch (e) {
			console.error(e)
		}
	  };

	return (
		<Modal
			open={openModal}
			title='Manual Transaction'
			headerStyle='border-b-1 py-3 mb-0'
			onClose={onCloseModal}
			contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[50%]'
		>
			<div className='min-w-[95%] md:min-w-[70%] lg:min-w-[60%] min-h-[400px]'>
				<div className=" bg-white rounded-2xl">
					<h2 className="text-xl font-semibold mb-4">Add Manual Transaction</h2>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div>
						<label className="block text-sm font-medium mb-1">
							Balance Type
						</label>
						<select
							{...register('transactionType', {
							required: 'Please select a balance type.',
							})}
							className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						>
							<option value="">Select type</option>
							<option value="Opening Balance">Opening Balance</option>
							<option value="Closing Balance">Closing Balance</option>
							<option value="POS">POS</option>
							<option value="PSP">PSP</option>
							<option value="Other">Other</option>
						</select>
						{errors.transactionType && (
							<p className="text-red-500 text-xs mt-1">
							{errors.transactionType.message}
							</p>
						)}
						</div>
						<div>
						<Controller 
							name='transactionDate'
							control={control}
							render={({ field: { onChange, onBlur, value, ref } }) => (
								<DatePopover
									label='Transaction Date'
									value={value}
									onChange={onChange}
								/>
							)}
						/>
						{errors.transactionDate && (
							<p className="text-red-500 text-xs mt-1">
							{errors.transactionDate.message}
							</p>
						)}
						</div>
						<div>
						<label className="block text-sm font-medium mb-1">
							Transaction Type
						</label>
						<div className="flex gap-4">
							<label className="flex items-center gap-2">
							<input
								type="radio"
								value="debit"
								{...register('direction', {
								required: 'Please choose Debit or Credit.',
								})}
							/>
							Debit
							</label>
							<label className="flex items-center gap-2">
							<input
								type="radio"
								value="credit"
								{...register('direction', {
								required: 'Please choose Debit or Credit.',
								})}
							/>
							Credit
							</label>
						</div>
						{errors.direction && (
							<p className="text-red-500 text-xs mt-1">
							{errors.direction.message}
							</p>
						)}
						</div>
						<div>
						<label className="block text-sm font-medium mb-1">
							Amount
						</label>
						<input
							type="number"
							step="0.01"
							placeholder="0.00"
							{...register('amount', {
							required: 'Please enter the transaction amount.',
							valueAsNumber: true,
							min: {
								value: 0.01,
								message: 'Amount must be greater than 0.',
							},
							})}
							className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
						{errors.amount && (
							<p className="text-red-500 text-xs mt-1">
							{errors.amount.message}
							</p>
						)}
						</div>
						<div>
						<label className="block text-sm font-medium mb-1">
							Description / Reference
						</label>
						<textarea
							rows={3}
							placeholder=""
							{...register('description', {
							required: 'Please enter a description or reference.',
							minLength: {
								value: 5,
								message: 'Description must be at least 5 characters long.',
							},
							})}
							className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
						></textarea>
						{errors.description && (
							<p className="text-red-500 text-xs mt-1">
							{errors.description.message}
							</p>
						)}
						</div>
						<button
						type="submit"
						className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition"
						>
							{transactionIsLoading && <Loading />}
							Save Transaction
						</button>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default ManualTransactionModal;